import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { MspTenantSummary } from '@iot-dashboard/shared';
import { useMspOverview } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { Panel } from '../../components/Panel';
import { StatRow, StatTile } from '../../components/StatTile';
import { StatusDot, toneFor } from '../../components/StatusDot';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';

const Count = ({ n, status }: { n: number; status: 'online' | 'alerting' | 'offline' | 'dormant' }) => (
  <span className="status" title={status}>
    <span className={`dot dot--${toneFor(status)}`} aria-hidden="true" />
    <span className="tabular">{n}</span>
    <span className="sr-only">{status}</span>
  </span>
);

export function MspPortalPage() {
  const overview = useMspOverview();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (overview.data?.tenants ?? []).filter((t) => !needle || t.tenant.name.toLowerCase().includes(needle) || t.tenant.slug.includes(needle));
  }, [overview.data, q]);

  const columns: Column<MspTenantSummary>[] = [
    { key: 'name', header: 'Tenant', primary: true, sortValue: (r) => r.tenant.name, render: (r) => (
      <div>
        <div className="cell-primary">{r.tenant.name}</div>
        <div className="cell-sub">{r.tenant.slug}</div>
      </div>
    ) },
    { key: 'status', header: 'Status', sortValue: (r) => r.tenant.status, render: (r) => <StatusDot status={r.tenant.status} /> },
    { key: 'sites', header: 'Sites', align: 'right', sortValue: (r) => r.sites, render: (r) => r.sites },
    { key: 'devices', header: 'Sensors', align: 'right', sortValue: (r) => r.totalDevices, render: (r) => r.totalDevices },
    { key: 'online', header: 'Online', align: 'right', sortValue: (r) => r.devices.online, render: (r) => <Count n={r.devices.online} status="online" /> },
    { key: 'alerting', header: 'Alerting', align: 'right', sortValue: (r) => r.devices.alerting, render: (r) => <Count n={r.devices.alerting} status="alerting" /> },
    { key: 'offline', header: 'Offline', align: 'right', sortValue: (r) => r.devices.offline, render: (r) => <Count n={r.devices.offline} status="offline" /> },
    { key: 'dormant', header: 'Dormant', align: 'right', sortValue: (r) => r.devices.dormant, render: (r) => <Count n={r.devices.dormant} status="dormant" /> },
    { key: 'alerts', header: 'Open alerts', align: 'right', sortValue: (r) => r.openAlerts, render: (r) => (r.openAlerts > 0 ? <strong>{r.openAlerts}</strong> : <span className="muted">0</span>) },
    { key: 'sync', header: 'Last sync', sortValue: (r) => r.lastSyncAt ?? '', render: (r) => (
      <span className="row">
        <StatusDot status={r.lastSyncStatus} label={false} />
        <TimeAgo iso={r.lastSyncAt} />
      </span>
    ) },
    { key: 'open', header: '', render: (r) => <Link to={`/t/${r.tenant.tenantId}/overview`} className="btn btn--sm" onClick={(e) => e.stopPropagation()}>Open</Link> },
  ];

  if (overview.isPending) return <Loading label="Loading tenants…" />;
  if (overview.isError) return <ErrorState error={overview.error} retry={() => overview.refetch()} />;
  const { totals } = overview.data;

  return (
    <>
      <PageHeader
        title="MSP portal"
        subtitle="Every client tenant at a glance. Open a tenant to see its sites, sensors and alerts."
        actions={
          <Link to="/msp/tenants/new" className="btn btn--primary">
            <Plus size={16} /> Create tenant
          </Link>
        }
      />
      <StatRow>
        <StatTile label="Tenants" value={totals.tenants} tone="accent" />
        <StatTile label="Sensors online" value={totals.devices.online} tone="good" />
        <StatTile label="Sensors alerting" value={totals.devices.alerting} tone="warning" />
        <StatTile label="Sensors offline" value={totals.devices.offline} tone="critical" />
        <StatTile label="Dormant" value={totals.devices.dormant} tone="neutral" />
        <StatTile label="Open alerts" value={totals.openAlerts} tone={totals.openAlerts > 0 ? 'critical' : 'good'} />
      </StatRow>
      <Panel
        title="Tenants"
        flush
        actions={<input className="input" style={{ width: 240 }} placeholder="Filter tenants" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter tenants" />}
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.tenant.tenantId}
          onRowClick={(r) => navigate(`/t/${r.tenant.tenantId}/overview`)}
          defaultSort={{ key: 'name', dir: 'asc' }}
          refetching={overview.isFetching}
          empty={<div className="empty"><h3>No tenants yet</h3><p>Create the first client tenant to get started.</p></div>}
        />
      </Panel>
    </>
  );
}
